import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    ButtonGroup,
} from '@mui/material';
import Modal from '../components/Modal';

import CreateTask from '../components/tasks/CreateTask';
import TaskList from '../components/tasks/TaskList';
import TaskDetails from '../components/tasks/TaskDetails';

export default function Tasks() {
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
    const [taskDetailsId, setTaskDetailsId] = useState(null);
    const [taskDetails, setTaskDetails] = useState(null);
    const [taskContext, setTaskContext] = useState(null);
    const [detailsError, setDetailsError] = useState(null);
    const [disableDetails, setDisableDetails] = useState(false);
    const [detailsLogs, setDetailsLogs] = useState([]);
    const [detailsComments, setDetailsComments] = useState([]);
    const [updateListSignal, setUpdateListSignal] = useState(0);
    const [selectedView, setSelectedView] = useState('board');

    const fetchTaskDetails = async () => {
        try {
            setDetailsLoading(true);
            setDisableDetails(true);
            setDetailsError(null);
            const res = await fetch(`/api/tasks/${taskDetailsId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) throw new Error('Failed to fetch task details');
            const data = await res.json();
            setTaskDetails(data);
        } catch (error) {
            console.error('Error fetching task details:', error);
            setDetailsError('Failed to load task details. Please try again.');
        } finally {
            setDetailsLoading(false);
            setDisableDetails(false);
        }
    }

    const fetchTaskLogs = async () => {
        try {
            const res = await fetch(`/api/tasks/${taskDetailsId}/logs`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) throw new Error('Failed to fetch task logs');
            const data = await res.json();
            setDetailsLogs(data);
        } catch (error) {
            console.error('Error fetching task logs:', error);
            setDetailsError('Failed to load task logs. Please try again.');
            setDetailsLogs([]);
        }
    }

    const fetchTaskComments = async () => {
        try {
            const res = await fetch(`/api/tasks/${taskDetailsId}/comments`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) throw new Error('Failed to fetch task comments');
            const data = await res.json();
            setDetailsComments(data);
        } catch (error) {
            console.error('Error fetching task comments:', error);
            setDetailsError('Failed to load task comments. Please try again.');
            setDetailsComments([]);
        }
    }

    const fetchTaskContext = async () => {
        try {
            const res = await fetch(`/api/tasks/${taskDetailsId}/detail`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) throw new Error('Failed to fetch task context');
            const data = await res.json();
            setTaskContext(data);
        } catch (error) {
            console.error('Error fetching task context:', error);
            setTaskContext(null);
        }
    }

    const postComment = async (comment) => {
        try {
            setDisableDetails(true);
            const res = await fetch(`/api/tasks/${taskDetailsId}/comments`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ comment }),
            });
            if (!res.ok) throw new Error('Failed to post comment');
            const data = await res.json();
            setDetailsComments((prev) => [data, ...prev]);
            fetchTaskLogs();
        } catch (error) {
            console.error('Error posting comment:', error);
            setDetailsError('Failed to post comment. Please try again.');
        } finally {
            setDisableDetails(false);
        }
    }

    const postAction = async (action, listId) => {
        try {
            setDisableDetails(true);
            const res = await fetch(`/api/tasks/${listId ?? taskDetailsId}/actions`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(action),
            });
            if (!res.ok) throw new Error('Failed to post action');
            const data = await res.json();
            setTaskDetails((prev) => ({ ...prev, updatedAt: data.updatedAt, state: data.state, priority: data.priority, assignedUser: data.assignedUser, assignedUserName: data.assignedUserName }));
            taskDetailsId && fetchTaskLogs();
            setUpdateListSignal((prev) => prev + 1);
        } catch (error) {
            console.error('Error posting action:', error);
            setDetailsError('Failed to post action. Please try again.');
        } finally {
            setDisableDetails(false);
        }
    }

    const putEdit = async (updatedTask) => {
        try {
            setDisableDetails(true);
            const res = await fetch(`/api/tasks/${taskDetailsId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedTask),
            });
            if (!res.ok) throw new Error('Failed to edit task');
            const data = await res.json();
            setTaskDetails(prev => ({ ...prev, name: data.name, description: data.description, dueDate: data.dueDate }));
            fetchTaskLogs();
            setUpdateListSignal((prev) => prev + 1);
        } catch (error) {
            console.error('Error editing task:', error);
            setDetailsError('Failed to edit task. Please try again.');
        } finally {
            setDisableDetails(false);

        }
    }

    useEffect(() => {
        if (!taskDetailsId) return;
        fetchTaskDetails();
        fetchTaskContext();
        fetchTaskLogs();
        fetchTaskComments();
    }, [taskDetailsId]);

    const handleOpenCreateModal = () => setCreateModalOpen(true);
    const handleCloseCreateModal = () => setCreateModalOpen(false);

    const handleCloseTaskDetailsModal = () => {
        setTaskDetailsOpen(false);
        setTaskDetailsId(null);
    }

    const handleOpenTaskDetails = (id) => {
        if (!id) return;
        setTaskDetailsOpen(true);
        setTaskDetailsId(id);
    }

    const handleChangeState = (state, listId = null) => {
        const payload = {
            actionName: 'setstate',
            state,
        }
        postAction(payload, listId);
    }

    const handleTaskEdit = (updatedTask) => {
        console.log('Handling task edit:', updatedTask);
        if (updatedTask.name !== taskDetails.name
            || updatedTask.description !== taskDetails.description
            || updatedTask.dueDate !== taskDetails.dueDate
        ) {
            if (updatedTask.name === '') {
                setDetailsError('Task name cannot be empty.');
                return;
            }
            putEdit({
                name: updatedTask.name,
                description: updatedTask.description,
            });
        }

        if (updatedTask.priority !== taskDetails.priority) {
            const payload = {
                actionName: 'setpriority',
                priority: updatedTask.priority,
            }
            postAction(payload);
        }

        if (updatedTask.assignedUser !== taskDetails.assignedUser) {
            const payload = {
                actionName: 'assign',
                assignedUser: updatedTask.assignedUser,
                assignedUserName: updatedTask.assignedUserName,
            }
            postAction(payload);
        }
    }

    return (
        <Box sx={{ p: 3, height: 'calc(100vh - 40px)', overflowY: 'auto', overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>Create New Task</Button>
                <ButtonGroup variant="outlined" aria-label="Basic button group">
                    <Button
                        variant={selectedView == 'list' ? 'contained' : 'outlined'}
                        onClick={() => setSelectedView('list')}
                    >
                        List
                    </Button>
                    <Button
                        variant={selectedView == 'board' ? 'contained' : 'outlined'}
                        onClick={() => setSelectedView('board')}
                    >
                        Board
                    </Button>
                </ButtonGroup>
            </Box>
            <Box sx={{ mt: 3 }}>
                <TaskList
                    onRowClick={handleOpenTaskDetails}
                    updateSignal={updateListSignal}
                    view={selectedView}
                    onChangeState={handleChangeState}
                />
            </Box>
            <Modal
                open={createModalOpen}
                onClose={handleCloseCreateModal}
                title="Create new task"
                size="small"
            >
                <CreateTask onClose={() => {
                    handleCloseCreateModal();
                    setUpdateListSignal((prev) => prev + 1);
                }} />
            </Modal>
            <Modal
                open={taskDetailsOpen}
                onClose={handleCloseTaskDetailsModal}
                title=''
                size="medium"
                disable={disableDetails}
            >
                {detailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {detailsError && <Alert severity="error">{detailsError}</Alert>}

                        {taskDetails ? (
                            <TaskDetails
                                task={taskDetails}
                                logs={detailsLogs}
                                comments={detailsComments}
                                context={taskContext}
                                onChangeState={handleChangeState}
                                onAddComment={postComment}
                                onEditTask={handleTaskEdit}
                            />
                        ) : (
                            <Alert severity="info">No task details available.</Alert>
                        )}
                    </>
                )}
            </Modal>
        </Box>
    )
}
